/**
 * Color math for email documents. Every surface that draws a release (the email HTML, the admin
 * canvas, the web archive, the mobile reader) needs the same tints, and an email cannot use
 * `rgba()` or `color-mix()`: Outlook's Word engine drops both, which would turn a soft button
 * into an invisible one. So tints are blended down to a solid hex here, once.
 */

const clamp = (value: number): number => Math.min(255, Math.max(0, Math.round(value)));

/** #abc, #aabbcc and #aabbccdd all parse; anything else falls back to black. */
const toRgb = (hex: string): [number, number, number] => {
  const value = hex.replace("#", "");
  const full =
    value.length === 3
      ? value
          .split("")
          .map((char) => char + char)
          .join("")
      : value.slice(0, 6);
  if (full.length !== 6 || /[^0-9a-fA-F]/.test(full)) return [0, 0, 0];
  return [parseInt(full.slice(0, 2), 16), parseInt(full.slice(2, 4), 16), parseInt(full.slice(4, 6), 16)];
};

const toHex = (rgb: [number, number, number]): string =>
  `#${rgb.map((channel) => clamp(channel).toString(16).padStart(2, "0")).join("")}`;

/**
 * `ratio` of `color` over `background`, as a solid hex. `mixHex("#4f46e5", "#ffffff", 0.1)` is the
 * 10% tint you would write as `rgba(79, 70, 229, 0.1)` on the web.
 */
export const mixHex = (color: string, background: string, ratio: number): string => {
  const weight = Math.min(1, Math.max(0, ratio));
  const [r1, g1, b1] = toRgb(color);
  const [r2, g2, b2] = toRgb(background);
  return toHex([
    r1 * weight + r2 * (1 - weight),
    g1 * weight + g2 * (1 - weight),
    b1 * weight + b2 * (1 - weight),
  ]);
};

/** Rough luma test, so text stays legible on whatever backdrop the document picked. */
export const isDarkHex = (hex: string): boolean => {
  const [r, g, b] = toRgb(hex);
  return 0.299 * r + 0.587 * g + 0.114 * b < 128;
};

/** Readable ink for a filled surface of `background`. */
export const contrastInk = (background: string): string => (isDarkHex(background) ? "#ffffff" : "#111827");

/** Up to two initials from an attribution, for the testimonial byline. Empty when there is none. */
export const initialsFrom = (name: string): string =>
  name
    .split(/[\s,]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? "")
    .join("");
